import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "./components/Header";
import { StepCompany } from "./steps/StepCompany";
import { StepIvalidCompanyCreditRisk } from "./steps/StepInvalidCompanyCreditRisk";
import { StepPreconditionCheckFailed } from "./steps/StepPreconditionCheckFailed";
import { StepContactThanks } from "./steps/StepContactThanks";
import { StepIdentification } from "./steps/StepIdentification";
import { StepError } from "./steps/StepError";
import { BasketForm, BasketOrderForm, BasketType, PreconditionFailureReason, basketService } from "./BasketService";
import { CompanyNameId } from "./Company";
import { ContactInformation } from "./ContactInformation";
import { Spinner } from "./components/Spinner";
import { StepBasketSelection } from "./steps/StepBasketSelection";
import { ProcessStep, orderNavigationItems } from "./navigation";
import { OrderNavi } from "./components/OrderNavi";
import { StepOrderInformation } from "./steps/StepOrderInformation";
import { StepSummary } from "./steps/StepSummary";
import { StepFinished } from "./steps/StepFinished";
import { translationKey } from "./i18n";
import { requireValue } from "./common";

export type AppStage = "start" | "authSuccess" | "authCanceled" | "authError";

const stageInitialSteps: Record<AppStage, ProcessStep> = {
	start: ProcessStep.SelectCompany,
	authSuccess: ProcessStep.BasketSelection,
	authCanceled: ProcessStep.Identification,
	authError: ProcessStep.Error
};

export const App = ({ returnUrl, stage }: { returnUrl: string; stage: AppStage }) => {
	const [step, setStep] = useState<ProcessStep>(stageInitialSteps[stage]);
	const [loading, setLoading] = useState(stage === "authSuccess" || stage === "authCanceled");
	const [company, setCompany] = useState<CompanyNameId>();
	const [basketForm, setBasketForm] = useState<BasketForm>({} as BasketForm);
	const [companyCheckFailureReason, setCompanyCheckFailureReason] = useState<PreconditionFailureReason | undefined>();
	const [errorMessage, setErrorMessage] = useState<translationKey | undefined>(
		stage === "authError" ? "identificationError" : undefined
	);
	const { t } = useTranslation();

	const showNavigation = orderNavigationItems.find((i) => i.step === step);

	useEffect(() => {
		if (stage !== "authSuccess" && stage !== "authCanceled") {
			return;
		}

		basketService
			.getFormState()
			.then((state) => {
				if (!state) {
					setStep(ProcessStep.SelectCompany);
					return;
				}

				if (stage === "authCanceled") {
					setCompany({
						id: state.businessId,
						name: state.companyName,
						displayName: state.companyDisplayName
					});
					setStep(ProcessStep.Identification);
				} else {
					setBasketForm(prev => ({
						...prev,
						...state
					}));

					if (state.preconditionFailureReason) {
						setCompany({
							id: state.businessId,
							name: state.companyName,
							displayName: state.companyDisplayName
						});
						setCompanyCheckFailureReason(state.preconditionFailureReason);
						setStep(ProcessStep.PreconditionFailed);
					}
				}
			})
			.catch(() => {
				setErrorMessage("formDataLoadingFailed");
				setStep(ProcessStep.Error);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [stage]);

	const companySelected = async (company: CompanyNameId) => {
		setCompany({
			...company
		});

		const checkResult = await basketService.checkCompany(company);
		if (!checkResult) {
			setErrorMessage("companyCheckFailed");
			setStep(ProcessStep.Error);
			return;
		}

		if (!checkResult.validCompany) {
			setCompanyCheckFailureReason(checkResult.failureReason);
			if (checkResult.failureReason === PreconditionFailureReason.CREDIT_RISK) {
				setStep(ProcessStep.CreditRisk);
			} else {
				setStep(ProcessStep.PreconditionFailed);
			}
		} else {
			setStep(ProcessStep.Identification);
		}
	};

	const identify = () => {
		window.location.href = "/autom_asiakas/BasketsTunnistaudu";
	};

	const sendContactInformation = async (contactInformation: ContactInformation) => {
		const success = await basketService.sendContactInformation(contactInformation);
		if (success) {
			setStep(ProcessStep.ContactThanks);
		} else {
			setErrorMessage("contactFormFailed");
			setStep(ProcessStep.Error);
		}
	};

	const onBasketSelected = (basketType: BasketType) => {
		setBasketForm({
			...basketForm,
			basketType: basketType
		});
		setStep(ProcessStep.OrderInformation);
	};

	const onOrdeInfoEntered = (newBasketForm: BasketForm) => {
		setBasketForm({
			...basketForm,
			...newBasketForm
		});
		setStep(ProcessStep.Summary);
	};

	const getTrackingProductName = (basketType: BasketType) => {
		if (basketType === BasketType.Micro) {
			return "Palvelupaketit Micro";
		} else if (basketType === BasketType.Small) {
			return "Palvelupaketit Small";
		} else {
			return "Palvelupaketit Compact";
		}
	};

	const getAnalyticsTransactionId = () => {
		const randomSuffix = Math.floor(Math.random() * 1000000 + 1);
		return `${new Date().getTime()}-${randomSuffix}`;
	};

	const sendAnalyticsEvent = (basketForm: BasketOrderForm) => {
		globalThis.dataLayer.push({
			"event": "purchase",
			"ecommerce": {
				"purchase": {
					"actionField": {
						"revenue": t(`price${basketForm.basketType}`),
						"id": getAnalyticsTransactionId()
					},
					"products":[{
						"name": getTrackingProductName(basketForm.basketType),
						"price": t(`price${basketForm.basketType}`),
						"quantity": 1
					}]
				}
			}
		});
	};

	const onFinishOrder = async () => {
		const result = await basketService.placeOrder(basketForm as BasketOrderForm);
		if (result.success) {
			setStep(ProcessStep.Finished);
			sendAnalyticsEvent(basketForm);
		} else {
			setErrorMessage(result.errorMessage);
			setStep(ProcessStep.Error);
		}
	};

	const renderStep = () => {
		switch (step) {
			case ProcessStep.SelectCompany:
				return <StepCompany returnUrl={returnUrl} company={company} selectCompany={companySelected} />;
			case ProcessStep.CreditRisk:
				return <StepIvalidCompanyCreditRisk />;
			case ProcessStep.PreconditionFailed:
				return (
					<StepPreconditionCheckFailed
						returnUrl={returnUrl}
						company={requireValue(company)}
						firstName={basketForm.firstName}
						lastName={basketForm.lastName}
						reason={companyCheckFailureReason}
						sendContactInformation={sendContactInformation}
						onBack={() => setStep(ProcessStep.SelectCompany)}
					/>
				);
			case ProcessStep.ContactThanks:
				return <StepContactThanks returnUrl={returnUrl} />;
			case ProcessStep.Identification:
				return (
					<StepIdentification
						returnUrl={returnUrl}
						identity={identify}
						onBack={() => setStep(ProcessStep.SelectCompany)}
					/>
				);
			case ProcessStep.BasketSelection:
				return (
					<StepBasketSelection
						returnUrl={returnUrl}
						onNext={onBasketSelected}
					/>
				);
			case ProcessStep.OrderInformation:
				return (
					<StepOrderInformation
						returnUrl={returnUrl}
						initialBasketForm={basketForm}
						onBack={() => setStep(ProcessStep.BasketSelection)}
						onNext={onOrdeInfoEntered}
					/>
				);
			case ProcessStep.Summary:
				return (
					<StepSummary
						returnUrl={returnUrl}
						onBack={() => setStep(ProcessStep.OrderInformation)}
						onNext={onFinishOrder}
						basketForm={basketForm}
					/>
				);
			case ProcessStep.Finished:
				return <StepFinished />;
			case ProcessStep.Error:
				return <StepError errorText={requireValue(errorMessage)} />;
		}
	};

	return (
		<>
			<Header returnUrl={returnUrl} />
			<div className="wrapper">
				<div className="container">
					<div className="row">
						<div className="col-md-12 col-sm-12 col-xs-12">
							<h1>{t("title")}</h1>
						</div>
					</div>
					{showNavigation && <OrderNavi items={orderNavigationItems} active={step} />}
					{loading ? <Spinner /> : renderStep()}
				</div>
			</div>
		</>
	);
};
